/**
 * Studios on the wire (PLAN §5.1, §7.1a, D22, D32): the "rig" — its devices
 * and connections, the mappings from a show style's layers to those devices,
 * and settings — as one versioned document per studio. What an operator
 * plays (piece types, layers, panels, actions) is the show style's
 * (`showStyles.ts`); a production picks a studio and a show style.
 *
 * **The contract carries the ENVELOPE, not the config's shape.** `config` is
 * an opaque JSON object here (`configDocument.ts`); what a valid studio config
 * is belongs to the engine (`@acp/engine/studio.js`). The handlers validate
 * `config` with an injected validator (`ConfigValidators` in
 * `@acp/handlers/ports.js`); a failing config is `validation-failed` with the
 * validator's issues, prefixed `config.`.
 *
 * **The default show style.** `defaultShowStyleId` names the show style a new
 * production of this studio gets when it names none, and the one the SPA
 * pre-selects; null for none. It must name an existing show style.
 *
 * **Optimistic concurrency.** Every write bumps `version` by one; an update
 * (`PUT`) names the version it was made from and is refused (`conflict`) when
 * the stored studio has moved on — re-read, re-apply, retry. A delete may name
 * one too (`?version=`).
 *
 * **Snapshots (D22).** A production copies the studio's config when it is
 * created (`productions.ts`), so editing a studio never changes a production
 * that already exists.
 *
 * `.strict()` everywhere, as the rest of the contract; ids are key segments
 * (`entityIdWireSchema`: no `#`), so a studio id can never forge a DynamoDB key.
 */
import { z } from 'zod';
import { configDocumentWireSchema, MAX_PRODUCTION_CONFIG_CHARS } from './configDocument.js';
import { isoTimestampWireSchema, showStyleIdWireSchema, siteIdWireSchema, studioIdWireSchema } from './primitives.js';

/**
 * The studio config document: a JSON object, otherwise opaque to the contract (the module comment). The ENVELOPE
 * keeps the pre-D32 bound (a stored studio written before the split may be a whole studio config of up to that
 * size, and must still read — the migration exists to split exactly those; review finding). What a write may store
 * is the studio budget, `MAX_STUDIO_CONFIG_CHARS`, which the handlers' validator enforces (`configValidators.ts`).
 */
export const studioConfigWireSchema = configDocumentWireSchema(MAX_PRODUCTION_CONFIG_CHARS, 'studio config');
export type StudioConfigWire = z.infer<typeof studioConfigWireSchema>;

const studioNameSchema = z.string().trim().min(1).max(200);

/** A studio without its config: what a listing shows. */
export const studioSummaryWireSchema = z
  .object({
    studioId: studioIdWireSchema,
    name: studioNameSchema,
    /** The site it belongs to, or null for a studio tied to no site (resource scope, D14). */
    siteId: siteIdWireSchema.nullable(),
    /** The show style its productions get unless they name one, and the SPA pre-selects (D32); null for none. Absent before D32: null. */
    defaultShowStyleId: showStyleIdWireSchema.nullable().default(null),
    /** 1 on create, +1 per write: the optimistic-concurrency token. */
    version: z.number().int().positive(),
    createdAt: isoTimestampWireSchema,
    updatedAt: isoTimestampWireSchema,
    /** Who wrote this version (the token's subject). */
    updatedBy: z.string().min(1).max(128),
  })
  .strict();
export type StudioSummaryWire = z.infer<typeof studioSummaryWireSchema>;

/** A studio with its config. */
export const studioWireSchema = studioSummaryWireSchema.extend({ config: studioConfigWireSchema }).strict();
export type StudioWire = z.infer<typeof studioWireSchema>;

export const createStudioRequestWireSchema = z
  .object({
    /** Client-chosen id (a retried create returns the studio as it is); the server mints a ULID when absent. */
    studioId: studioIdWireSchema.optional(),
    name: studioNameSchema,
    siteId: siteIdWireSchema.nullable(),
    /** The default show style (D32); absent = none. */
    defaultShowStyleId: showStyleIdWireSchema.nullable().optional(),
    config: studioConfigWireSchema,
  })
  .strict();
export type CreateStudioRequestWire = z.infer<typeof createStudioRequestWireSchema>;

export const updateStudioRequestWireSchema = z
  .object({
    /** The version this update was made from; the stored one must still be it (`conflict` otherwise). */
    version: z.number().int().positive(),
    name: studioNameSchema,
    siteId: siteIdWireSchema.nullable(),
    /** The default show style (D32); absent = unchanged, null = none. */
    defaultShowStyleId: showStyleIdWireSchema.nullable().optional(),
    config: studioConfigWireSchema,
  })
  .strict();
export type UpdateStudioRequestWire = z.infer<typeof updateStudioRequestWireSchema>;

export const studioPathWireSchema = z.object({ studioId: studioIdWireSchema.describe('The studio’s id.') }).strict();

export const deleteStudioQueryWireSchema = z
  .object({
    version: z.coerce.number().int().positive().optional().describe('Delete only if the studio is still at this version (`conflict` otherwise).'),
  })
  .strict();

export const studioResponseWireSchema = z.object({ studio: studioWireSchema }).strict();
export const listStudiosResponseWireSchema = z.object({ studios: z.array(studioSummaryWireSchema) }).strict();
